var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { isNotNullOrEmpty, isBoolean, get, typeOf } from '@pcty/utils';
import Outlier from './private/Outlier';
import { useTable } from './private/TableContext';
import { Td as Component, Input as XInput, InputDisabled } from './Table.styled';
import { alignCenter, alignLeft, alignRight, divider as dividerCss, dividerBefore as dividerBeforeCss, tdThCheckbox, textDisabled, truncate as truncateCss, } from '../styles/legacy';
import { tdThCheckbox as tdThCheckboxCitron, textDisabled as textDisabledCitron } from '../styles/citron';
import { useTheme } from '@citrus/user';
var VERSION = '6.12.2';
var Td = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'left' : _c, ariaLabel = _a["aria-label"], automationId = _a.automationId, checked = _a.checked, children = _a.children, className = _a.className, colSpan = _a.colSpan, css = _a.css, _d = _a.disabled, disabled = _d === void 0 ? false : _d, divider = _a.divider, dividerBefore = _a.dividerBefore, _e = _a.hidden, hidden = _e === void 0 ? false : _e, _f = _a.onChange, onChange = _f === void 0 ? function () { return undefined; } : _f, _g = _a.outlier, outlier = _g === void 0 ? false : _g, 
    // NOTE: Do NOT remove `outlierAnalyzer`, Even though it is not used in this file, it is used in parent files.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    outlierAnalyzer = _a.outlierAnalyzer, render = _a.render, _h = _a.truncate, truncate = _h === void 0 ? -1 : _h, _j = _a.__editing, __editing = _j === void 0 ? false : _j, __rowIndex = _a.__rowIndex, _k = _a.__TYPE, TYPE = _k === void 0 ? 'Td' : _k, other = __rest(_a, ["align", 'aria-label', "automationId", "checked", "children", "className", "colSpan", "css", "disabled", "divider", "dividerBefore", "hidden", "onChange", "outlier", "outlierAnalyzer", "render", "truncate", "__editing", "__rowIndex", "__TYPE"]);
    var isCitron = useTheme().citrus2022;
    var renderState = useTable().renderState;
    var content = render
        ? render(children, { disabled: disabled, editing: __editing, renderState: renderState, rowIndex: __rowIndex })
        : children;
    if (get(content, '$$typeof', '').toString() !== 'Symbol(react.element)' &&
        ['string', 'array', 'number'].indexOf(typeOf(content)) === -1) {
        content = undefined;
    }
    var emotionObj = (_b = {},
        _b[emotionCss(alignCenter)] = align === 'center',
        _b[emotionCss(alignLeft)] = align === 'left',
        _b[emotionCss(alignRight)] = align === 'right',
        _b[emotionCss(isCitron ? textDisabledCitron : textDisabled)] = disabled && typeof content === 'string',
        _b[emotionCss(truncateCss(truncate))] = truncate >= 0,
        _b[emotionCss(isCitron ? tdThCheckboxCitron : tdThCheckbox)] = TYPE === 'TdCheckbox',
        _b[emotionCss(dividerCss)] = divider !== null && divider !== void 0 ? divider : false,
        _b[emotionCss(dividerBeforeCss)] = dividerBefore !== null && dividerBefore !== void 0 ? dividerBefore : false,
        _b[emotionCss(css)] = isNotNullOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        alignCenter: emotionObj[emotionCss(alignCenter)],
        alignLeft: emotionObj[emotionCss(alignLeft)],
        alignRight: emotionObj[emotionCss(alignRight)],
        textDisabled: emotionObj[emotionCss(isCitron ? textDisabledCitron : textDisabled)],
        truncate: emotionObj[emotionCss(truncateCss(truncate))],
        tdThCheckbox: emotionObj[emotionCss(isCitron ? tdThCheckboxCitron : tdThCheckbox)],
        dividerCss: emotionObj[emotionCss(dividerCss)],
        dividerBeforeCss: emotionObj[emotionCss(dividerBeforeCss)],
    };
    if (hidden)
        return null;
    return (React.createElement(Component, __assign({ className: classes, colSpan: colSpan, styleNames: getStyleNames(styleNames) }, other, getAutomation(automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION)),
        renderInput(typeof ariaLabel === 'function' && children
            ? ariaLabel(children, { renderState: renderState, rowIndex: __rowIndex })
            : ariaLabel === null || ariaLabel === void 0 ? void 0 : ariaLabel.toString(), disabled, checked, onChange, TYPE),
        renderContent(content, outlier, TYPE)));
};
var renderContent = function (content, outlier, TYPE) {
    if (TYPE === 'TdCheckbox' || !outlier)
        return content;
    return (React.createElement(Outlier, __assign({ key: new Date().getTime() }, (isBoolean(outlier) ? {} : outlier)), content));
};
var renderInput = function (ariaLabel, disabled, checked, onChange, TYPE) {
    if (TYPE !== 'TdCheckbox')
        return;
    var Input = disabled ? InputDisabled : XInput;
    return React.createElement(Input, { "aria-label": ariaLabel, checked: checked, disabled: disabled, onChange: onChange, type: "checkbox" });
};
Td.defaultProps = {
    __TYPE: 'Td',
};
export default Td;
